import React, { useEffect, useState, useRef } from 'react'
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragOverlay,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { SortableItem } from '@/components/common/SortableItem'
import _ from 'lodash'

const CATEGORY_ICONS = {
    'Contact fields': 'bi-chat-text',
    'User Information': 'bi-person',
    'Demographic questions': 'bi-people',
    'Work information': 'bi-briefcase',
    'National ID number': 'bi-hash',
}

const SYSTEM_QUESTION_CATEGORIES = {
    'Contact fields': [
        'EMAIL',
        'PHONE',
        'STREET_ADDRESS',
        'CITY',
        'STATE',
        'PROVINCE',
        'COUNTRY',
        'POST_CODE',
        'ZIP',
    ],
    'User Information': ['FULL_NAME'],
    'Demographic questions': ['DOB', 'GENDER', 'RELATIONSHIP_STATUS', 'COUNTRY'],
    'Work information': ['COMPANY', 'JOB_TITLE'],
}

const SYSTEM_TYPES = Object.values(SYSTEM_QUESTION_CATEGORIES).flat()

// Generate Facebook API compatible labels for question types
const getDefaultLabel = (type) => {
    const labelMap = {
        // Contact fields
        'EMAIL': 'Email Address',
        'PHONE': 'Phone Number',
        'STREET_ADDRESS': 'Street Address',
        'CITY': 'City',
        'STATE': 'State',
        'PROVINCE': 'Province',
        'COUNTRY': 'Country',
        'POST_CODE': 'Postal Code',
        'ZIP': 'ZIP Code',
        
        // User Information
        'FULL_NAME': 'Full Name',
        
        // Demographic questions
        'DOB': 'Date of Birth',
        'GENDER': 'Gender',
        'RELATIONSHIP_STATUS': 'Marital Status',
        
        // Work information
        'COMPANY': 'Company',
        'JOB_TITLE': 'Job Title',
    }
    
    return labelMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
}

export function SystemQuestionBuilder({ questions: externalQuestions = [], onChange = () => {}, className = '' }) {
    const [systemQuestions, setSystemQuestions] = useState([])
    const [activeId, setActiveId] = useState(null)
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 5 },
            axis: 'y',
        }),
    )
    const [showMenu, setShowMenu] = useState(false)
    const [activeCategory, setActiveCategory] = useState(null)
    const menuRef = useRef(null)

    useEffect(() => {
        const systemQs = externalQuestions.filter((q) => SYSTEM_TYPES.includes(q.type))
        setSystemQuestions(systemQs)
    }, [externalQuestions])

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowMenu(false)
                setActiveCategory(null)
            }
        }

        const handleEsc = (e) => {
            if (e.key === 'Escape') {
                setShowMenu(false)
                setActiveCategory(null)
            }
        }

        if (showMenu) {
            document.addEventListener('mousedown', handleClickOutside)
            document.addEventListener('keydown', handleEsc)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            document.removeEventListener('keydown', handleEsc)
        }
    }, [showMenu])

    const sync = (updatedSystemQuestions) => {
        const remaining = externalQuestions.filter((q) => !SYSTEM_TYPES.includes(q.type))
        const final = [...remaining, ...updatedSystemQuestions]
        setSystemQuestions(updatedSystemQuestions)
        onChange(final)
    }

    const updateQuestion = (id, updates) => {
        const updated = systemQuestions.map((q) => (q.id === id ? { ...q, ...updates } : q))
        sync(updated)
    }

    const removeQuestion = (id) => {
        const updated = systemQuestions.filter((q) => q.id !== id)
        sync(updated)
    }

    const addQuestion = (type) => {
        if (systemQuestions.some((q) => q.type === type)) return

        const newQuestion = {
            id: crypto.randomUUID(), // Keep for frontend tracking
            key: type.toLowerCase(), // Facebook API requires this
            type,
            label: getDefaultLabel(type), // Facebook API requires this
            optional: false,
        }

        sync([...systemQuestions, newQuestion])
    }

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={({ active }) => setActiveId(active.id)}
            onDragEnd={({ active, over }) => {
                setActiveId(null)
                if (active.id !== over?.id) {
                    const oldIndex = systemQuestions.findIndex((q) => q.id === active.id)
                    const newIndex = systemQuestions.findIndex((q) => q.id === over.id)
                    const reordered = arrayMove(systemQuestions, oldIndex, newIndex)
                    sync(reordered)
                }
            }}
        >
            <div className="tw:pb-4">
                <SortableContext items={systemQuestions.map((q) => q.id)} strategy={verticalListSortingStrategy}>
                    {systemQuestions.map((q) => (
                        <SortableItem key={q.id} id={q.id}>
                            {({ listeners }) => (
                                <div
                                    className={`${className} tw:border-bottom-white-1 tw:pb-4 tw:pt-4 tw:w-full tw:flex tw:items-center tw:justify-between tw:px-3`}
                                >
                                    <div className="tw:flex tw:items-center tw:gap-2 tw:text-white tw:font-semibold">
                                        <i
                                            {...listeners}
                                            className="bi bi-grip-vertical tw:text-slate-500 pr-1 tw:cursor-grab tw:active:cursor-grabbing"
                                        />
                                        {_.capitalize(q.type.replaceAll('_', ' '))}
                                    </div>
                                    <div className="tw:flex tw:items-center tw:gap-3">
                                        <input
                                            type="checkbox"
                                            checked={q.optional}
                                            onChange={() => updateQuestion(q.id, { optional: !q.optional })}
                                        />
                                        <label className="tw:flex tw:text-sm tw:text-white">
                                            <span>Optional</span>
                                        </label>
                                        <button
                                            onClick={() => removeQuestion(q.id)}
                                            className="tw:pl-5 tw:pr-2 tw:text-blue-400 tw:hover:text-red-500"
                                        >
                                            <i className="bi bi-trash" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </SortableItem>
                    ))}
                </SortableContext>

                <DragOverlay>
                    {activeId ? (
                        <div className="tw:w-[300px] tw:bg-slate-700 tw:px-3 tw:py-4 tw:rounded tw:flex tw:items-center tw:justify-between">
                            <div className="tw:flex tw:items-center tw:gap-2 tw:text-white tw:font-semibold">
                                <i className="bi bi-grip-vertical tw:text-slate-500 pr-1" />
                                {_.capitalize(systemQuestions.find((q) => q.id === activeId)?.type.replaceAll('_', ' ') || '')}
                            </div>
                        </div>
                    ) : null}
                </DragOverlay>

                <div className="tw:pl-3 tw:mt-6 tw:pl-1 tw:relative tw:inline-block tw:text-left tw:relative">
                    <button
                        type="button"
                        className="tw:bg-slate-900 tw:border tw:border-blue-500 tw:text-white tw:px-4 tw:py-2 tw:shadow-lg tw:flex tw:items-center tw:gap-1 tw:rounded-none"
                        onClick={() => setShowMenu((prev) => !prev)}
                    >
                        <i className="bi bi-plus" />
                        Add Category
                        <i className="bi bi-caret-down-fill tw:text-xs tw:ml-1" />
                    </button>

                    {showMenu && (
                        <div
                            ref={menuRef}
                            className="tw:absolute tw:top-full tw:left-0 tw:z-100 tw:mt-2 tw:w-72 tw:rounded-none tw:bg-slate-900 tw:border tw:border-blue-500 tw:shadow-xl tw:p-2 tw:space-y-1"
                            onMouseLeave={() => setActiveCategory(null)}
                        >
                            {Object.entries(SYSTEM_QUESTION_CATEGORIES).map(([category, types]) => (
                                <div
                                    key={category}
                                    className="tw:relative"
                                    onMouseEnter={() => setActiveCategory(category)}
                                >
                                    <div className="tw:flex tw:items-center tw:text-white tw:font-medium tw:px-3 tw:py-2 tw:hover:bg-slate-700 tw:rounded-md tw:cursor-pointer">
                                        <i className={`bi ${CATEGORY_ICONS[category]} tw:mr-3 tw:text-base`} />
                                        <span>{category}</span>
                                    </div>
                                    {activeCategory === category && (
                                        <div
                                            className="tw:absolute tw:left-full tw:top-0 tw:ml-2 tw:w-56 tw:rounded-none tw:bg-slate-900 tw:border tw:border-blue-500 tw:px-2 tw:pt-1 tw:pb-1 tw:shadow-xl tw:z-50"
                                            onMouseLeave={() => setActiveCategory(null)}
                                            onMouseEnter={() => setActiveCategory(category)}
                                            style={
                                                activeCategory === 'Contact fields'
                                                    ? { position: 'absolute', top: '-100px' }
                                                    : {}
                                            }
                                        >
                                            {types.map((type) => (
                                                <button
                                                    key={type}
                                                    onClick={() => {
                                                        addQuestion(type)
                                                        setShowMenu(false)
                                                    }}
                                                    className="tw:block tw:px-2 tw:py-2 tw:text-sm tw:text-white tw:hover:bg-slate-700  tw:hover:!rounded-md tw:!rounded-md tw:w-full tw:text-left"
                                                >
                                                    {_.capitalize(type.replaceAll('_', ' '))}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </DndContext>
    )
}
